/**
 * Fond d'accueil : un tableau assombri sur lequel une retouche de demonstration
 * se rejoue en boucle. C'est le tutoriel implicite du CLAUDE.md : on comprend le
 * jeu avant d'avoir lu une ligne.
 *
 * Deux choses sont tirees au hasard a chaque chargement, le tableau et la
 * position de la zone. Un fond identique visite apres visite ne raconte plus
 * "n'importe quelle piece de n'importe quelle œuvre", il raconte une image fixe.
 *
 * La palette n'est PAS la palette par defaut : elle est prelevee dans l'œuvre,
 * autour de la zone. Peinte en couleurs generiques, la demonstration ressortait
 * comme un gribouillis noir pose sur le tableau au lieu d'une piece qui cherche
 * a se fondre.
 *
 * La boucle tourne en rAF hors React, et se met en pause quand l'onglet passe en
 * arriere-plan : un fond decoratif n'a aucune raison de consommer du CPU quand
 * personne ne le regarde.
 */
import { useEffect, useRef, type ReactElement } from 'react';
import type { DrawZone } from '@retusch/shared';
import { paintings, loadArtwork, naturalSize } from '../game/artwork.js';
import { prepareArtwork, sampleZonePalette } from '../gallery/renderArtwork.js';

/** Duree d'un cycle complet : dessin, pause, effacement. */
const LOOP_MS = 12_000;
/** Part du cycle consacree au trace. */
const DRAW_FRACTION = 0.55;
/** Part du cycle ou la retouche reste affichee, avant de repartir a vide. */
const HOLD_FRACTION = 0.85;
/**
 * Hauteur d'ecran visee pour le CENTRE de la zone, en fraction de la fenetre.
 * Au-dessus du titre, donc loin du formulaire et des boutons.
 */
const BACKDROP_TARGET_Y = 0.14;
/** Cote de la zone de demonstration, en fraction de l'image. */
const DEMO_ZONE_SIZE = 0.13;
/**
 * Bornes horizontales du tirage. Le fond est rendu en `cover` : sur un portrait
 * etroit, seule la bande centrale du tableau reste a l'ecran. Une zone tiree
 * jusqu'aux bords y serait invisible une fois sur deux.
 */
const DEMO_ZONE_X_MIN = 0.3;
const DEMO_ZONE_X_MAX = 0.57;
/** Marge verticale : une zone a cheval sur le bord se lit mal. */
const DEMO_ZONE_MARGIN = 0.08;
/** Taille de la palette prelevee. Doit couvrir les colorIndex du dessin de demo. */
const DEMO_PALETTE_SIZE = 10;
/**
 * Categories admises en fond d'accueil : les moins chargees. L'accueil montre
 * une oeuvre lisible d'un coup d'oeil, pas la plus dense du catalogue.
 */
const BACKDROP_CATEGORIES = [1, 2];

/** Zone carree tiree au hasard, toujours entierement dans l'image. */
function randomZone(): DrawZone {
  const span = 1 - DEMO_ZONE_SIZE - DEMO_ZONE_MARGIN * 2;
  return {
    x: DEMO_ZONE_X_MIN + Math.random() * (DEMO_ZONE_X_MAX - DEMO_ZONE_X_MIN),
    y: DEMO_ZONE_MARGIN + Math.random() * span,
    width: DEMO_ZONE_SIZE,
    height: DEMO_ZONE_SIZE,
  };
}

/**
 * Decalage vertical qui amene le centre de la zone a BACKDROP_TARGET_Y.
 *
 * Un decalage constant ne pouvait pas marcher : la zone est tiree au hasard, et
 * le cadrage `cover` ne montre qu'une bande du tableau. Une zone haute tombait
 * alors au-dessus de l'ecran, et la demonstration se jouait dans le vide.
 */
function focusYFor(
  box: { width: number; height: number },
  imageSize: { width: number; height: number },
  zone: DrawZone,
): number {
  const scale = Math.max(box.width / imageSize.width, box.height / imageSize.height);
  const drawnHeight = imageSize.height * scale;
  const centerY = (zone.y + zone.height / 2) * drawnHeight;
  const base = (box.height - drawnHeight) / 2;
  const wanted = (BACKDROP_TARGET_Y * box.height - centerY - base) / box.height;

  // Le decalage est BORNE par les bords de l'image : sans cela, une zone basse
  // faisait remonter le tableau au-dela de son propre bord inferieur et laissait
  // une bande noire en bas de l'ecran. Quand la zone ne peut pas remonter assez,
  // c'est le cadrage qui gagne : mieux vaut une zone un peu plus bas qu'un trou.
  const highest = -base / box.height; // haut de l'image cale sur le haut de la boite
  const lowest = (box.height - drawnHeight - base) / box.height; // bas cale sur le bas
  return Math.min(Math.max(wanted, lowest), highest);
}

export function ArtworkBackdrop(): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    let alive = true;
    let raf = 0;
    let onResize: (() => void) | null = null;

    const run = async (): Promise<void> => {
      // Seuls les traits viennent du fichier : le tableau et la zone sont tires
      // ici, la demonstration doit pouvoir tomber sur n'importe quelle œuvre.
      const drawing = await fetch('/demo/home-replay.bin').then(async (response) => {
        if (!response.ok) throw new Error('dessin de demo introuvable');
        return await response.arrayBuffer();
      });
      if (!alive) return;

      // Ordre aleatoire plutot qu'un tirage unique, pour qu'un tableau dont
      // l'image manque cede la place au suivant au lieu d'eteindre le fond.
      const catalog = paintings()
        .filter((painting) => BACKDROP_CATEGORIES.includes(painting.category))
        .sort(() => Math.random() - 0.5);
      let loaded: { file: string; image: HTMLImageElement } | null = null;
      for (const painting of catalog) {
        try {
          loaded = { file: painting.file, image: await loadArtwork(painting.file) };
          break;
        } catch {
          continue;
        }
      }
      if (!alive) return;
      if (loaded === null) throw new Error('aucune image de tableau disponible');

      const zone = randomZone();
      const prepared = await prepareArtwork({
        file: loaded.file,
        zone,
        palette: sampleZonePalette(
          loaded.image,
          naturalSize(loaded.image),
          zone,
          DEMO_PALETTE_SIZE,
        ),
        drawing,
      });
      if (!alive) return;

      const imageSize = naturalSize(loaded.image);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let box = { width: 1, height: 1 };

      const resize = (): void => {
        const bounds = canvas.getBoundingClientRect();
        box = {
          width: Math.round(Math.max(bounds.width, 1) * dpr),
          height: Math.round(Math.max(bounds.height, 1) * dpr),
        };
      };
      resize();
      onResize = resize;
      window.addEventListener('resize', resize);

      const startedAt = performance.now();
      let lastProgress = -1;

      const frame = (now: number): void => {
        if (!alive) return;

        // Onglet cache : on ne calcule rien, on redemande simplement une frame
        // (que le navigateur ne servira pas tant que l'onglet dort).
        if (document.hidden) {
          raf = requestAnimationFrame(frame);
          return;
        }

        const phase = ((now - startedAt) % LOOP_MS) / LOOP_MS;
        const progress =
          phase < DRAW_FRACTION ? phase / DRAW_FRACTION : phase < HOLD_FRACTION ? 1 : 0;

        // Ne recomposer que si l'image change vraiment : pendant la pause, la
        // boucle ne coute plus rien.
        if (Math.abs(progress - lastProgress) > 0.004) {
          lastProgress = progress;
          prepared.setProgress(progress);
          // focusY cadre sur la zone tiree, pour qu'elle tombe au-dessus du
          // titre et jamais derriere les boutons du formulaire.
          prepared.paint(canvas, {
            size: box.width,
            viewport: box,
            focusY: focusYFor(box, imageSize, zone),
            // Lisere or : sans lui, la zone assombrie par le voile se lit comme
            // une tache et non comme la mecanique du jeu.
            badge: true,
          });
        }

        raf = requestAnimationFrame(frame);
      };

      raf = requestAnimationFrame(frame);
    };

    void run().catch(() => {
      // Assets de demo absents : le fond reste uni, l'accueil fonctionne.
    });

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      if (onResize !== null) window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="backdrop" aria-hidden="true">
      <canvas className="backdrop__canvas" ref={canvasRef} />
      <div className="backdrop__veil" />
    </div>
  );
}
